import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { streamPublicChat } from '@/services/chatApi';
import { renderMarkdownBold } from '@/utils/renderMarkdown';

const BRAND_GOLD = '#c9a84c';
const BRAND_DARK = '#0a1225';

/* ─── BILINGUAL WIDGET TEXTS ─── */
const WIDGET_TEXTS = {
  es: {
    agentName: 'Freedy',
    subtitle: 'Freedom Homes Club',
    online: 'En línea',
    placeholder: 'Escribe tu pregunta...',
    disclaimer: 'Este chat es solo informativo y no constituye asesoramiento financiero.',
    error: 'Lo siento, ha ocurrido un error. Por favor, intenta de nuevo.',
    openLabel: 'Abrir chat de asistencia',
    initialMessage: '¡Hola! Soy **Freedy**, el asistente virtual de **Freedom Homes Club**. Puedo contarte cómo funciona el club, qué incluye cada membresía, la formación y los eventos. ¿En qué puedo ayudarte?',
    quickQuestions: [
      '¿Qué es Freedom Homes Club?',
      '¿Cómo funciona la tokenización?',
      '¿Qué incluye cada membresía?',
      'Quiero ser miembro Premium',
    ],
  },
  en: {
    agentName: 'Freedy',
    subtitle: 'Freedom Homes Club',
    online: 'Online',
    placeholder: 'Type your question...',
    disclaimer: 'This chat is for informational purposes only and does not constitute financial advice.',
    error: "I'm sorry, an error occurred. Please try again.",
    openLabel: 'Open support chat',
    initialMessage: 'Hi! I\'m **Freedy**, the virtual assistant of **Freedom Homes Club**. I can tell you how the club works, what each membership includes, the training and the events. How can I help you?',
    quickQuestions: [
      'What is Freedom Homes Club?',
      'How does tokenization work?',
      'What does each membership include?',
      'I want to be a Premium member',
    ],
  },
};

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
          isUser
            ? 'text-[#0a1225] font-medium rounded-br-sm'
            : 'text-gray-100 rounded-bl-sm border border-white/10'
        }`}
        style={{
          backgroundColor: isUser ? BRAND_GOLD : 'rgba(255,255,255,0.07)',
          whiteSpace: 'pre-wrap',
        }}
      >
        {msg.content ? renderMarkdownBold(msg.content) : <span className="opacity-50 animate-pulse">▌</span>}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start mb-3">
      <div className="px-4 py-3 rounded-2xl rounded-bl-sm border border-white/10 bg-white/[0.07]">
        <div className="flex gap-1 items-center">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PublicChatWidget() {
  // Sync with language selected on the main page via localStorage + custom event
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem('fhc-lang') || 'es'; } catch { return 'es'; }
  });

  useEffect(() => {
    const handleLangChange = (e) => setLang(e.detail);
    window.addEventListener('fhc-lang-change', handleLangChange);
    return () => window.removeEventListener('fhc-lang-change', handleLangChange);
  }, []);

  const wt = WIDGET_TEXTS[lang] || WIDGET_TEXTS.es;

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [{ role: 'assistant', content: WIDGET_TEXTS[lang]?.initialMessage || WIDGET_TEXTS.es.initialMessage }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Reset initial message when language changes (only if conversation hasn't started)
  useEffect(() => {
    const initialMsg = { role: 'assistant', content: WIDGET_TEXTS[lang]?.initialMessage || WIDGET_TEXTS.es.initialMessage };
    setMessages((prev) => (prev.length === 1 ? [initialMsg] : prev));
  }, [lang]);

  useEffect(() => {
    if (open) {
      setUnread(false);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const content = (text || input).trim();
    if (!content || loading) return;

    const userMsg = { role: 'user', content };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    await streamPublicChat(newMessages, {
      onChunk: (chunk) => {
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'assistant',
            content: updated[updated.length - 1].content + chunk,
          };
          return updated;
        });
      },
      onDone: () => setLoading(false),
      onError: () => {
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'assistant',
            content: wt.error,
          };
          return updated;
        });
        setLoading(false);
      },
    });
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const showQuickQuestions = messages.length === 1;

  return (
    <>
      {/* Floating bubble button */}
      <motion.button
        onClick={() => setOpen((o) => !o)}
        className="fhc-public-chat fixed bottom-24 md:bottom-6 right-6 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center z-[9998] focus:outline-none"
        style={{ backgroundColor: BRAND_GOLD }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        aria-label={wt.openLabel}
        aria-expanded={open}
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <X size={22} className="text-[#0a1225]" />
            </motion.div>
          ) : (
            <motion.div key="open" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <MessageCircle size={22} className="text-[#0a1225]" />
            </motion.div>
          )}
        </AnimatePresence>
        {unread && !open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white" />
        )}
      </motion.button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fhc-public-chat fixed bottom-[168px] md:bottom-24 right-6 w-[360px] max-w-[calc(100vw-24px)] max-h-[calc(100vh-188px)] md:max-h-[calc(100vh-120px)] z-[9997] rounded-2xl overflow-hidden shadow-2xl border border-white/10 flex flex-col"
            style={{ height: '520px', backgroundColor: BRAND_DARK }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/[0.08]" style={{ backgroundColor: 'rgba(201,168,76,0.08)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: BRAND_GOLD }}>
                <MessageCircle size={16} className="text-[#0a1225]" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">{wt.agentName}</p>
                <p className="text-gray-500 text-xs">{wt.subtitle}</p>
              </div>
              <div className="ml-auto flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs text-gray-500">{wt.online}</span>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
              {messages.map((msg, i) => (
                <MessageBubble key={i} msg={msg} />
              ))}
              {loading && messages[messages.length - 1]?.content === '' && (
                <TypingIndicator />
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick questions */}
            {showQuickQuestions && (
              <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                {wt.quickQuestions.map((q) => (
                  <button
                    key={q}
                    onClick={() => sendMessage(q)}
                    disabled={loading}
                    className="text-xs px-2.5 py-1 rounded-full border border-white/20 text-gray-300 hover:border-[#c9a84c]/60 hover:text-[#c9a84c] transition-colors disabled:opacity-40"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="px-3 pb-3 pt-2 border-t border-white/[0.06]">
              <div className="flex items-end gap-2 bg-white/[0.06] rounded-xl px-3 py-2 border border-white/10 focus-within:border-[#c9a84c]/40 transition-colors">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder={wt.placeholder}
                  disabled={loading}
                  rows={1}
                  className="flex-1 bg-transparent text-white text-sm placeholder-gray-500 resize-none focus:outline-none"
                  style={{ maxHeight: '80px', overflowY: 'auto' }}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={loading || !input.trim()}
                  aria-label="Enviar"
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-30"
                  style={{ backgroundColor: BRAND_GOLD }}
                >
                  {loading ? (
                    <Loader2 size={14} className="animate-spin text-[#0a1225]" />
                  ) : (
                    <Send size={14} className="text-[#0a1225]" />
                  )}
                </button>
              </div>
              <p className="text-center text-[10px] text-gray-600 mt-1.5">{wt.disclaimer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
